import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { UiModalComponent } from '@components';
import {
    ClientErrorCodes,
    ILicenseRequest,
    LicenseEnvironment,
    LicenseFormControl,
    LicenseType,
    ModalResultType,
    OfflineState,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { FileUploadService } from '@services';

import {
    BehaviorSubject,
    Subject,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    templateUrl: './activate-offline.modal.html',
    styleUrls: [
        './activate-offline.modal.scss',
        '../../../../../styles/modal/file-upload.modal.scss',
    ],
    providers: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class ActivateOfflineModalComponent
    extends UiModalComponent<ILicenseRequest>
    implements OnInit, OnDestroy {

    public OfflineState = OfflineState;
    public LicenseFormControl = LicenseFormControl;

    @Input()
    public editPermission: string[] = [];

    @Input()
    public isHost = false;

    @Input()
    public activationRequest!: string;

    public hasFile$ = new BehaviorSubject<OfflineState>(OfflineState.UNDETERMINED);

    public file: File | null = null;
    public formGroup!: FormGroup;

    public progress = 15;
    public hasValidLicense = OfflineState.YES;

    public serviceUrl?: string;

    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);
    private _translateService = this._injector.get<TranslateService>(TranslateService);
    private _fileUploadService = this._injector.get<FileUploadService>(FileUploadService);
    private _destroyed$ = new Subject();

    ngOnInit() {
        this.context.data = {
            installationId: this.context.data.installationId,
            licenseKey: '',
            environment: LicenseEnvironment.Production,
            update: this.context.data.update,
        } as ILicenseRequest;

        this.formGroup = this._injector.get<FormBuilder>(FormBuilder).group({
            [LicenseFormControl.installationId]: [
                this.context.data.installationId,
            ],
            [LicenseFormControl.licenseKey]: [
                this.context.data.licenseKey,
                Validators.required,
            ],
            [LicenseFormControl.environment]: [
                this.context.data.environment,
            ],
        });

        this.serviceUrl = this._appState.settings.get(Setting.serviceUrl);

        this.hasFile$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(hasFile => {
                switch (hasFile) {
                    case OfflineState.UNDETERMINED:
                        this.progress = 30;
                        break;
                    case OfflineState.NO:
                        this.progress = 60;
                        break;
                    case OfflineState.YES:
                    case OfflineState.GENERATE:
                        this.progress = 90;
                        break;
                }
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public get fileName() {
        return this.file ? this.file.name : '';
    }

    public clipboardCopied() {
        this._snack.success(this._translateService.instant('global_message_copied_clipboard'));
    }

    public onClear() {
        this.file = null;
    }

    public onFileChange(files: File[]) {
        this.file = files[0];
    }

    public generate() {
        const payload = {
            installationId: this.context.data.installationId,
            licenseKey: this.formGroup.get(LicenseFormControl.licenseKey)!.value,
            environment: this.formGroup.get(LicenseFormControl.environment)!.value,
        } as ILicenseRequest;

        this.customEvent.emit({
            type: ModalResultType.Generic,
            data: payload,
        });
    }

    public onSubmit() {
        if (!this._fileUploadService.isFileItemValid(this.file!, null, 102400)) {
            if (this.file) {
                this._snack.error(
                    `${this._translateService.instant('settings_license_form_upload_invalid_warning')} (#${
                    ClientErrorCodes.InvalidLicenseFormat
                    })`,
                );
            }
            return;
        }

        this._dialogRef.close({
            type: ModalResultType.Add,
            additionalData: {
                file: this.file!,
                type: LicenseType.UiPath,
            },
        });
    }
}
